import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Link,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Container,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import unifiedApi from '../../utils/unifiedApi';
import useAutoSave from '../../hooks/useAutoSave';

const SLOTS_PER_PAGE = 10;

const getGameAvailabilityDisplay = (game) => {
  const homeTeamName = game.homeTeamName?.trim() || '';
  const awayTeamName = game.awayTeamName?.trim() || '';
  const isByeWeek = Boolean(homeTeamName && !awayTeamName);

  return {
    homeTeam: { name: homeTeamName, slug: game.homeTeamSlug },
    awayTeam: { name: awayTeamName, slug: game.awayTeamSlug },
    isByeWeek,
  };
};

const getHeaderTeams = (data) => {
  if (data.teams?.length) return data.teams;
  return (data.teamNames || []).map((name) => ({ name, slug: null }));
};

const TeamNameLink = ({ name, slug, onClick }) => {
  if (!slug) return name;
  return (
    <Link
      component={RouterLink}
      to={`/teams/${slug}`}
      color="primary"
      underline="hover"
      onClick={(event) => onClick?.(event, slug)}
    >
      {name}
    </Link>
  );
};

/**
 * PlayerAvailabilityResponsePage - Anonymous page for players to set their game availability via token link
 */
const PlayerAvailabilityResponsePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [data, setData] = useState(null);
  const [availabilities, setAvailabilities] = useState({});
  const [spareAvailabilities, setSpareAvailabilities] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [changeCounter, setChangeCounter] = useState(0);
  const [slotPage, setSlotPage] = useState(0);
  const [socialEventAttending, setSocialEventAttending] = useState({});
  const [socialEventSaving, setSocialEventSaving] = useState({});
  const availabilitiesRef = useRef(availabilities);
  availabilitiesRef.current = availabilities;
  const spareAvailabilitiesRef = useRef(spareAvailabilities);
  spareAvailabilitiesRef.current = spareAvailabilities;

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load availability data from token
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await unifiedApi.getPlayerAvailabilityByToken(token, userTimezone);
      
      if (!response) {
        setError('Invalid or expired link. Please request a new availability link.');
        return;
      }
      
      setData(response);
      
      // Initialize local availability state from response
      const initialAvailabilities = {};
      response.availabilities.forEach(a => {
        initialAvailabilities[a.gameId] = a.isAvailable;
      });
      setAvailabilities(initialAvailabilities);

      const initialSpareAvailabilities = {};
      (response.spareAvailabilities || []).forEach(a => {
        initialSpareAvailabilities[`${a.sparePoolMemberId}:${a.spareTimeSlotId}`] = a.isAvailable;
      });
      setSpareAvailabilities(initialSpareAvailabilities);
      
      // Initialize local social event RSVP state
      const initialSocialEvents = {};
      (response.socialEvents || []).forEach(s => {
        initialSocialEvents[s.socialEventId] = s.isAttending;
      });
      setSocialEventAttending(initialSocialEvents);
    } catch (err) {
      console.error('Error loading availability data:', err);
      if (err.message?.includes('400') || err.message?.includes('expired')) {
        setError('This link has expired or is invalid. Please request a new availability link.');
      } else {
        setError('Failed to load availability data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, userTimezone]);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, loadData]);

  // Auto-save callback — reads availabilities from ref to avoid stale closures
  const performSave = useCallback(async () => {
    const availabilityList = Object.entries(availabilitiesRef.current).map(([gameId, isAvailable]) => ({
      gameId: parseInt(gameId),
      isAvailable
    }));
    const spareAvailabilityList = (data?.spareAvailabilities || []).map(availability => ({
      sparePoolMemberId: availability.sparePoolMemberId,
      spareTimeSlotId: availability.spareTimeSlotId,
      isAvailable: spareAvailabilitiesRef.current[`${availability.sparePoolMemberId}:${availability.spareTimeSlotId}`]
    }));
    await unifiedApi.submitPlayerAvailabilityByToken(token, availabilityList, spareAvailabilityList);
  }, [token, data]);

  const { saveStatus, triggerSave } = useAutoSave(performSave, hasChanges, {
    onSuccess: () => {
      setSuccess('Your availability has been saved.');
      setHasChanges(false);
    },
    onError: (err) => {
      console.error('Error submitting availability:', err);
      if (err.message?.includes('400') || err.message?.includes('expired')) {
        setError('This link has expired. Please request a new availability link.');
      } else {
        setError('Failed to save availability. Please try again.');
      }
    },
  }, changeCounter);

  // Same-tab navigation unmounts the page and would cancel the pending auto-save, so save first.
  const handleTeamLinkClick = async (event, slug) => {
    const opensNewTab = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    if (!hasChanges || opensNewTab) return;

    event.preventDefault();
    if (await triggerSave()) {
      navigate(`/teams/${slug}`);
    }
  };

  // Handle availability toggle
  const handleToggle = (gameId, newValue) => {
    setAvailabilities(prev => ({
      ...prev,
      [gameId]: newValue
    }));
    setHasChanges(true);
    setChangeCounter(c => c + 1);
    setSuccess(null);
  };

  const handleSpareToggle = (sparePoolMemberId, spareTimeSlotId, newValue) => {
    setSpareAvailabilities(prev => ({
      ...prev,
      [`${sparePoolMemberId}:${spareTimeSlotId}`]: newValue
    }));
    setHasChanges(true);
    setChangeCounter(c => c + 1);
    setSuccess(null);
  };

  // Handle social event RSVP toggle (saves immediately)
  const handleSocialEventToggle = async (socialEventId, attending) => {
    setSocialEventSaving(prev => ({ ...prev, [socialEventId]: true }));
    setError(null);
    try {
      await unifiedApi.submitSocialEventRsvpViaPlayerToken(token, socialEventId, attending);
      setSocialEventAttending(prev => ({ ...prev, [socialEventId]: attending }));
      setSuccess('Your RSVP has been saved.');
    } catch (err) {
      console.error('Error saving social event RSVP:', err);
      setError('Failed to save RSVP. Please try again.');
    } finally {
      setSocialEventSaving(prev => ({ ...prev, [socialEventId]: false }));
    }
  };

  // Format date/time for display
  const formatGameTime = (startTime) => {
    const date = new Date(startTime);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
    return { dateStr, timeStr };
  };

  const combinedAvailabilityItems = useMemo(() => {
    if (!data) {
      return [];
    }

    const gameItems = (data.availabilities || []).map(availability => ({
      id: `game-${availability.gameId}`,
      type: 'game',
      sortTime: availability.game.startTime,
      availability,
    }));

    const socialEventItems = (data.socialEvents || []).map(socialEvent => ({
      id: `social-${socialEvent.socialEventId}`,
      type: 'social',
      sortTime: socialEvent.start,
      socialEvent,
    }));

    const spareItems = (data.spareAvailabilities || []).map(availability => ({
      id: `spare-${availability.sparePoolMemberId}-${availability.spareTimeSlotId}`,
      type: 'spare',
      sortTime: availability.timeSlot.startTime,
      availability,
    }));

    const now = new Date();
    return [...gameItems, ...spareItems, ...socialEventItems]
      .filter(item => new Date(item.sortTime) >= now)
      .sort((a, b) => new Date(a.sortTime) - new Date(b.sortTime));
  }, [data]);

  // Render availability toggle buttons
  const renderToggle = (gameId, currentValue) => {
    return (
      <ToggleButtonGroup
        value={currentValue}
        exclusive
        onChange={(e, newValue) => {
          if (newValue !== null) {
            handleToggle(gameId, newValue);
          }
        }}
        size="small"
      >
        <ToggleButton 
          value={true} 
          sx={{ 
            '&.Mui-selected': { 
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              '&:hover': { backgroundColor: 'success.main' }
            }
          }}
        >
          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
          Yes
        </ToggleButton>
        <ToggleButton 
          value={false}
          sx={{ 
            '&.Mui-selected': { 
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              '&:hover': { backgroundColor: 'error.main' }
            }
          }}
        >
          <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
          No
        </ToggleButton>
      </ToggleButtonGroup>
    );
  };

  const renderSpareToggle = (availability, currentValue) => {
    return (
      <ToggleButtonGroup
        value={currentValue}
        exclusive
        onChange={(e, newValue) => {
          if (newValue !== null) {
            handleSpareToggle(availability.sparePoolMemberId, availability.spareTimeSlotId, newValue);
          }
        }}
        size="small"
      >
        <ToggleButton 
          value={true} 
          sx={{ 
            '&.Mui-selected': { 
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              '&:hover': { backgroundColor: 'success.main' }
            }
          }}
        >
          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
          Yes
        </ToggleButton>
        <ToggleButton 
          value={false}
          sx={{ 
            '&.Mui-selected': { 
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              '&:hover': { backgroundColor: 'error.main' }
            }
          }}
        >
          <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
          No
        </ToggleButton>
      </ToggleButtonGroup>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading your availability...</Typography>
        </Paper>
      </Container>
    );
  }

  // Error state (no data)
  if (error && !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Contact your league organizer to get a new availability link.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const headerTeams = getHeaderTeams(data);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            startIcon={<CloseIcon />}
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            size="small"
            color="inherit"
          >
            Close
          </Button>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Game Availability
          </Typography>
          <Typography variant="h6" color="primary">
            {data.memberName}
          </Typography>
          {headerTeams.length > 0 && (
            <Typography variant="body2" color="text.secondary" data-testid="header-teams">
              {headerTeams.map((team, index) => (
                <React.Fragment key={team.slug || team.name}>
                  {index > 0 && ', '}
                  <TeamNameLink name={team.name} slug={team.slug} onClick={handleTeamLinkClick} />
                </React.Fragment>
              ))}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Link expires: {new Date(data.expirationDate).toLocaleDateString()}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Select your availability for each item below. Click &quot;Yes&quot; if you can attend, &quot;No&quot; if you cannot.
        </Alert>

        {/* Availability list */}
        {combinedAvailabilityItems.length === 0 ? (
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No upcoming availability items.
          </Typography>
        ) : (() => {
          const totalSlots = combinedAvailabilityItems.length;

          if (totalSlots === 0) {
            return (
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No upcoming availability items.
              </Typography>
            );
          }

          const totalPages = Math.ceil(totalSlots / SLOTS_PER_PAGE);
          const pagedItems = combinedAvailabilityItems.slice(slotPage * SLOTS_PER_PAGE, (slotPage + 1) * SLOTS_PER_PAGE);

          return (
          <>
            {/* Pagination controls */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => setSlotPage(p => Math.max(0, p - 1))}
                  disabled={slotPage === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {slotPage * SLOTS_PER_PAGE + 1}&ndash;{Math.min((slotPage + 1) * SLOTS_PER_PAGE, totalSlots)} of {totalSlots}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setSlotPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={slotPage >= totalPages - 1}
                >
                  Next
                </Button>
              </Box>
            )}

            <List>
              {pagedItems.map((item, index) => {
                if (item.type === 'social') {
                  const socialEvent = item.socialEvent;
                  const { dateStr, timeStr } = formatGameTime(socialEvent.start);
                  const currentValue = socialEventAttending[socialEvent.socialEventId];
                  const saving = socialEventSaving[socialEvent.socialEventId];

                  return (
                    <React.Fragment key={item.id}>
                      <ListItem
                        sx={{
                          py: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 2 },
                          pr: { sm: 2 }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="medium">
                              {socialEvent.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {dateStr} at {timeStr}
                              </Typography>
                              {socialEvent.location && (
                                <Typography variant="body2" color="text.secondary">
                                  {socialEvent.location}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {socialEvent.scopeLabel}
                              </Typography>
                            </Box>
                          }
                          sx={{ minWidth: 200, flex: 1 }}
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          {(currentValue === null || currentValue === undefined) && (
                            <HelpOutlineIcon sx={{ color: 'warning.main' }} />
                          )}
                          <ToggleButtonGroup
                            value={currentValue}
                            exclusive
                            onChange={(e, newValue) => {
                              if (newValue !== null && !saving) {
                                handleSocialEventToggle(socialEvent.socialEventId, newValue);
                              }
                            }}
                            size="small"
                            disabled={saving}
                          >
                            <ToggleButton
                              value={true}
                              aria-label={`Yes for ${socialEvent.title}`}
                              sx={{
                                '&.Mui-selected': {
                                  backgroundColor: 'success.light',
                                  color: 'success.contrastText',
                                  '&:hover': { backgroundColor: 'success.main' }
                                }
                              }}
                            >
                              <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Yes
                            </ToggleButton>
                            <ToggleButton
                              value={false}
                              aria-label={`No for ${socialEvent.title}`}
                              sx={{
                                '&.Mui-selected': {
                                  backgroundColor: 'error.light',
                                  color: 'error.contrastText',
                                  '&:hover': { backgroundColor: 'error.main' }
                                }
                              }}
                            >
                              <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
                              No
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </ListItem>
                      {index < pagedItems.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                }

                if (item.type === 'spare') {
                  const availability = item.availability;
                  const { dateStr, timeStr } = formatGameTime(availability.timeSlot.startTime);
                  const currentValue = spareAvailabilities[`${availability.sparePoolMemberId}:${availability.spareTimeSlotId}`];

                  return (
                    <React.Fragment key={item.id}>
                      <ListItem
                        sx={{
                          py: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 2 },
                          pr: { sm: 2 }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="medium">
                              {dateStr}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {timeStr}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Spare schedule
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                League: {availability.leagueName}
                              </Typography>
                            </Box>
                          }
                          sx={{ minWidth: 200, flex: 1 }}
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          {currentValue === null || currentValue === undefined ? (
                            <HelpOutlineIcon sx={{ color: 'warning.main' }} />
                          ) : null}
                          {renderSpareToggle(availability, currentValue)}
                        </Box>
                      </ListItem>
                      {index < pagedItems.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                }

                const availability = item.availability;
                const { dateStr, timeStr } = formatGameTime(availability.game.startTime);
                const currentValue = availabilities[availability.gameId];
                const gameDisplay = getGameAvailabilityDisplay(availability.game);
                
                return (
                  <React.Fragment key={item.id}>
                    <ListItem 
                      sx={{ 
                        py: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1.5, sm: 2 },
                        pr: { sm: 2 }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="medium">
                            {dateStr}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {timeStr}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" data-testid="game-matchup">
                              <TeamNameLink {...gameDisplay.homeTeam} onClick={handleTeamLinkClick} />
                              {gameDisplay.isByeWeek ? ' BYE week' : (
                                <>
                                  {' vs '}
                                  <TeamNameLink {...gameDisplay.awayTeam} onClick={handleTeamLinkClick} />
                                </>
                              )}
                            </Typography>
                            {gameDisplay.isByeWeek && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Set your availability if you could spare for another team this week.
                              </Typography>
                            )}
                            {availability.game.venueName && (
                              <Typography variant="caption" color="text.secondary">
                                {availability.game.venueName}
                                {availability.game.fieldName ? ` - ${availability.game.fieldName}` : ''}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ minWidth: 200, flex: 1 }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {currentValue === null || currentValue === undefined ? (
                          <HelpOutlineIcon sx={{ color: 'warning.main' }} />
                        ) : null}
                        {renderToggle(availability.gameId, currentValue)}
                      </Box>
                    </ListItem>
                    {index < pagedItems.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>

            {/* Pagination controls (bottom) */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => setSlotPage(p => Math.max(0, p - 1))}
                  disabled={slotPage === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {slotPage * SLOTS_PER_PAGE + 1}&ndash;{Math.min((slotPage + 1) * SLOTS_PER_PAGE, totalSlots)} of {totalSlots}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setSlotPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={slotPage >= totalPages - 1}
                >
                  Next
                </Button>
              </Box>
            )}

            {/* Auto-save status and fallback save button */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              {saveStatus === 'saving' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Saving...</Typography>
                </Box>
              )}
              {saveStatus === 'saved' && !hasChanges && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CloudDoneIcon fontSize="small" color="success" />
                  <Typography variant="body2" color="success.main">All changes saved</Typography>
                </Box>
              )}
              {saveStatus === 'error' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudOffIcon fontSize="small" color="error" />
                    <Typography variant="body2" color="error.main">Failed to save</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={triggerSave}
                  >
                    Retry Save
                  </Button>
                </Box>
              )}
              {saveStatus === 'idle' && !hasChanges && !success && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Changes are saved automatically
                </Typography>
              )}
            </Box>
          </>
          );
        })()}
      </Paper>
    </Container>
  );
};

export default PlayerAvailabilityResponsePage;
